import { useEffect, useState } from "react";
import { AppointmentJoin, AppointmentLogJoin } from "@/repositories/appointments.repository";
import { fetchVendorAppointment, fetchVendorAppointmentLogs } from "./appointments.api";
import type { PhotoSlot } from "@shyne/shared/api";
import { AppointmentWorkflow } from "@/models/appointment-task.model";
import { EMPTY_CHECKLIST, type ChecklistState } from "@/lib/appointment-checklist";
import { AppointmentPhotoRepository } from "@/repositories/appointment-photos.repository";
import { AppointmentChecklistRepository } from "@/repositories/appointment-checklist.repository";
import { storedClock } from "@shyne/shared/api";
import { vendorClockPhoto } from "@/lib/appointment-field-actions";
import { appointmentFieldActions, reportFieldActionRefusal, reportPhotoRefusal, reportUploadFailure } from "@/services/appointment-field-actions.service";
import { extractPhotoMetadata } from "@/lib/photo-metadata";
import { toast } from "sonner";

export const useVendorAppointmentDetail = (id: string) => {
  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState<AppointmentJoin | null>(null);
  const [logs, setLogs] = useState<AppointmentLogJoin[]>([]);
  const [checklist, setChecklist] = useState<ChecklistState>(EMPTY_CHECKLIST);
  const [serviceWorkflows, setServiceWorkflows] = useState<PhotoSlot[]>([]);
  const [appointmentWorkflows, setAppointmentWorkflows] = useState<AppointmentWorkflow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [loadingSecondary, setLoadingSecondary] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [appt, logRows] = await Promise.all([fetchVendorAppointment(id), fetchVendorAppointmentLogs(id)]);
        if (!cancelled) {
          setAppointment(appt);
          setLogs(logRows);
          setLoading(false);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e.message ?? "Failed to load appointment");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const service = appointment === null ? null : (appointment.service?.id ?? "");

  useEffect(() => {
    if (service === null) return;
    let cancelled = false;
    setLoadingSecondary(true);
    (async () => {
      const results = await Promise.allSettled([
        service === "" ? Promise.resolve(null) : AppointmentPhotoRepository.slots(id),
        AppointmentChecklistRepository.read(id),
        AppointmentPhotoRepository.read(id),
      ]);
      if (cancelled) return;
      const [slots, checklistRead, photos] = results;
      if (slots.status === "fulfilled" && slots.value !== null) setServiceWorkflows(slots.value);
      if (checklistRead.status === "fulfilled") setChecklist(checklistRead.value);
      if (photos.status === "fulfilled") setAppointmentWorkflows(photos.value);
      results.forEach((result) => {
        if (result.status === "rejected") console.error("Failed to load the appointment's requirements:", result.reason);
      });
      setLoadingSecondary(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, service]);

  const refreshLogs = async () => {
    try {
      setLogs(await fetchVendorAppointmentLogs(id));
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to refresh the activity");
    }
  };

  const reloadAppointment = async () => {
    try {
      setAppointment(await fetchVendorAppointment(id));
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to reload appointment");
    }
  };

  const uploadPhoto = async (workflowId: string, file: File, isAfter: boolean) => {
    try {
      const metadata = await extractPhotoMetadata(file);
      setAppointmentWorkflows(await AppointmentPhotoRepository.recordSlotPhoto(id, { workflow: workflowId, isAfter, file, metadata }));
      toast.success(`${isAfter ? "After" : "Before"} photo uploaded`);
    } catch (err) {
      await reportPhotoRefusal(err, reReadAfterRefusedPhoto);
      throw err;
    }
  };

  const reReadAfterRefusedPhoto = async () => {
    const [, photos] = await Promise.all([reloadAppointment(), AppointmentPhotoRepository.read(id)]);
    setAppointmentWorkflows(photos);
  };

  const uploadedClockPhoto = async (type: "clockin" | "clockout", file: File) => {
    try {
      const photoUrl = await AppointmentPhotoRepository.upload(id, type === "clockin" ? "clockIn" : "clockOut", file);
      return vendorClockPhoto(type, photoUrl, await extractPhotoMetadata(file));
    } catch (err) {
      reportUploadFailure(err);
      throw err;
    }
  };

  const uploadClock = async (type: "clockin" | "clockout", file: File) => {
    const request = await uploadedClockPhoto(type, file);
    try {
      const recorded = await appointmentFieldActions.recordClockPhoto(id, request);
      const metaKey = type === "clockin" ? "clock_in" : "clock_out";
      const clock = storedClock(type === "clockin" ? recorded.clockIn : recorded.clockOut);
      setAppointment((prev) => prev && { ...prev, meta_data: { ...prev.meta_data, [metaKey]: clock } });
      toast.success(`${type === "clockin" ? "Start work" : "Completion"} photo uploaded`);
    } catch (err) {
      await reportFieldActionRefusal(err, "Failed to upload clock photo", reloadAppointment);
      throw err;
    }
  };

  return {
    loading,
    loadingSecondary,
    appointment,
    logs,
    serviceTasks: checklist.serviceTasks,
    serviceWorkflows,
    appointmentTasks: checklist.appointmentTasks,
    appointmentWorkflows,
    error,
    editOpen,
    setEditOpen,
    setAppointment,
    reloadAppointment,
    refreshLogs,
    uploadPhoto,
    uploadClock,
  };
};
